import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Text,
  Paper,
  Stack,
  Group,
  Table,
  Select,
  Button,
  Skeleton,
  SimpleGrid,
  Drawer,
  Divider,
  ActionIcon,
  Tooltip,
  Pagination,
  TextInput,
  Checkbox,
} from '@mantine/core';
import { useDisclosure, useDebouncedValue } from '@mantine/hooks';
import { DatePickerInput } from '@mantine/dates';
import {
  IconReceipt,
  IconCreditCard,
  IconDownload,
  IconChevronUp,
  IconChevronDown,
  IconSelector,
  IconEye,
  IconSearch,
  IconMail,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import PageHeader from '@/components/PageHeader';
import StatusBadge from '@/components/StatusBadge';
import StatCard from '@/components/StatCard';
import EmptyState from '@/components/EmptyState';
import { useInvoices, useInvoice, useDownloadInvoicePdf, useEmailInvoiceCopy, useBatchDownloadInvoices } from '@/hooks/usePortalApi';
import type { Invoice } from '@/types';
import dayjs from 'dayjs';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'paid', label: 'Paid' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'partial', label: 'Partial' },
];

type SortField = 'issued_date' | 'due_date' | 'amount' | 'balance_due' | 'status';
type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 20;

function SortIcon({ field, current, dir }: { field: SortField; current: SortField | null; dir: SortDir }) {
  if (current !== field) return <IconSelector size={14} style={{ opacity: 0.4 }} />;
  return dir === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />;
}

export default function Invoices() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [drawerInvId, setDrawerInvId] = useState<number | null>(null);
  const [drawerOpen, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const { data, isLoading } = useInvoices({
    status: status || undefined,
    search: debouncedSearch || undefined,
    date_from: dateRange[0] ? dayjs(dateRange[0]).format('YYYY-MM-DD') : undefined,
    date_to: dateRange[1] ? dayjs(dateRange[1]).format('YYYY-MM-DD') : undefined,
  });
  const { data: drawerInvoice } = useInvoice(drawerInvId ?? 0);
  const downloadPdf = useDownloadInvoicePdf();
  const emailCopy = useEmailInvoiceCopy();
  const batchDownload = useBatchDownloadInvoices();

  const allInvoices = data?.items ?? [];

  const sorted = useMemo(() => {
    if (!sortField) return allInvoices;
    return [...allInvoices].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'issued_date' || sortField === 'due_date') {
        cmp = dayjs(a[sortField]).unix() - dayjs(b[sortField]).unix();
      } else if (sortField === 'amount' || sortField === 'balance_due') {
        cmp = parseFloat(a[sortField]) - parseFloat(b[sortField]);
      } else {
        cmp = a.status.localeCompare(b.status);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [allInvoices, sortField, sortDir]);

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const invoices = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const totalOutstanding = allInvoices
    .filter((inv) => inv.status !== 'paid')
    .reduce((sum, inv) => sum + parseFloat(inv.balance_due || '0'), 0);

  const overdueCount = allInvoices.filter((inv) => inv.status === 'overdue').length;

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
    setPage(1);
  };

  const handleViewDrawer = (inv: Invoice) => {
    setDrawerInvId(inv.id);
    openDrawer();
  };

  const handleDownloadPdf = (invoiceId: number) => {
    downloadPdf.mutate(invoiceId, {
      onSuccess: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `invoice-${invoiceId}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      },
      onError: () => {
        notifications.show({
          title: 'Download failed',
          message: 'Unable to download PDF. Please try again.',
          color: 'red',
        });
      },
    });
  };

  const handleEmailCopy = (invoiceId: number) => {
    emailCopy.mutate(invoiceId, {
      onSuccess: () => {
        notifications.show({ title: 'Email sent', message: 'Invoice copy emailed to you.', color: 'green' });
      },
      onError: () => {
        notifications.show({ title: 'Email failed', message: 'Unable to send email. Please try again.', color: 'red' });
      },
    });
  };

  const handleBatchDownload = () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    batchDownload.mutate(ids, {
      onSuccess: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'invoices.zip';
        a.click();
        URL.revokeObjectURL(url);
        setSelectedIds(new Set());
      },
      onError: () => {
        notifications.show({ title: 'Download failed', message: 'Unable to download invoices.', color: 'red' });
      },
    });
  };

  const toggleSelected = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === invoices.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(invoices.map((inv) => inv.id)));
    }
  };

  const ThSort = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <Table.Th
      style={{ cursor: 'pointer', userSelect: 'none' }}
      onClick={() => toggleSort(field)}
    >
      <Group gap={4} wrap="nowrap">
        {children}
        <SortIcon field={field} current={sortField} dir={sortDir} />
      </Group>
    </Table.Th>
  );

  return (
    <Stack gap="lg">
      <PageHeader title="Invoices" description="View and pay your invoices" />

      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <StatCard
          title="Total Outstanding"
          value={`$${totalOutstanding.toFixed(2)}`}
          icon={<IconReceipt size={24} />}
        />
        <StatCard
          title="Total Invoices"
          value={String(data?.total ?? 0)}
          icon={<IconReceipt size={24} />}
        />
        <StatCard
          title="Overdue"
          value={String(overdueCount)}
          icon={<IconCreditCard size={24} />}
        />
      </SimpleGrid>

      <Group justify="space-between" wrap="wrap">
        <Group>
          {totalOutstanding > 0 && (
            <Button
              leftSection={<IconCreditCard size={16} />}
              onClick={() => navigate('/billing/pay?payAll=true')}
            >
              Pay All Outstanding (${totalOutstanding.toFixed(2)})
            </Button>
          )}
          {selectedIds.size > 0 && (
            <Button
              variant="light"
              leftSection={<IconDownload size={16} />}
              onClick={handleBatchDownload}
              loading={batchDownload.isPending}
            >
              Download Selected ({selectedIds.size})
            </Button>
          )}
        </Group>
        <Group>
          <TextInput
            placeholder="Search invoices..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => { setSearch(e.currentTarget.value); setPage(1); }}
            w={200}
            size="sm"
          />
          <DatePickerInput
            type="range"
            placeholder="Date range"
            value={dateRange}
            onChange={(v) => { setDateRange(v); setPage(1); }}
            w={240}
            size="sm"
            clearable
          />
          <Select
            placeholder="Filter by status"
            data={STATUS_OPTIONS}
            value={status}
            onChange={(v) => { setStatus(v ?? ''); setPage(1); }}
            w={160}
            size="sm"
            clearable
          />
        </Group>
      </Group>

      {isLoading ? (
        <Stack gap="xs">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} height={50} radius="md" />
          ))}
        </Stack>
      ) : invoices.length === 0 ? (
        <EmptyState
          icon={<IconReceipt size={30} />}
          title="No invoices found"
          description={status ? 'Try a different filter' : 'No invoices have been generated yet'}
        />
      ) : (
        <>
          <Paper>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={40}>
                    <Checkbox
                      checked={invoices.length > 0 && selectedIds.size === invoices.length}
                      indeterminate={selectedIds.size > 0 && selectedIds.size < invoices.length}
                      onChange={toggleSelectAll}
                      size="xs"
                    />
                  </Table.Th>
                  <Table.Th>Invoice #</Table.Th>
                  <ThSort field="issued_date">Issued</ThSort>
                  <ThSort field="due_date">Due Date</ThSort>
                  <ThSort field="status">Status</ThSort>
                  <ThSort field="amount"><Text ta="right" inherit>Amount</Text></ThSort>
                  <ThSort field="balance_due"><Text ta="right" inherit>Balance</Text></ThSort>
                  <Table.Th w={180} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {invoices.map((inv) => (
                  <Table.Tr key={inv.id}>
                    <Table.Td>
                      <Checkbox
                        checked={selectedIds.has(inv.id)}
                        onChange={() => toggleSelected(inv.id)}
                        size="xs"
                      />
                    </Table.Td>
                    <Table.Td>
                      <Text
                        size="sm"
                        fw={500}
                        c="brand"
                        component={Link}
                        to={`/invoices/${inv.id}`}
                        style={{ textDecoration: 'none' }}
                      >
                        {inv.invoice_number}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{dayjs(inv.issued_date).format('MMM D, YYYY')}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{dayjs(inv.due_date).format('MMM D, YYYY')}</Text>
                    </Table.Td>
                    <Table.Td>
                      <StatusBadge status={inv.status} />
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm">${parseFloat(inv.amount).toFixed(2)}</Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm" fw={500} c={parseFloat(inv.balance_due) > 0 ? 'red' : 'brand'}>
                        ${parseFloat(inv.balance_due).toFixed(2)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        <Tooltip label="Quick view">
                          <ActionIcon variant="subtle" size="sm" onClick={() => handleViewDrawer(inv)}>
                            <IconEye size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Download PDF">
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={() => handleDownloadPdf(inv.id)}
                            loading={downloadPdf.isPending}
                          >
                            <IconDownload size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Email Copy">
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={() => handleEmailCopy(inv.id)}
                            loading={emailCopy.isPending}
                          >
                            <IconMail size={16} />
                          </ActionIcon>
                        </Tooltip>
                        {inv.status !== 'paid' && parseFloat(inv.balance_due) > 0 && (
                          <Button
                            size="xs"
                            variant="light"
                            component={Link}
                            to={`/billing/pay?invoice=${inv.id}`}
                          >
                            Pay
                          </Button>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
          {totalPages > 1 && (
            <Group justify="center">
              <Pagination total={totalPages} value={page} onChange={setPage} />
            </Group>
          )}
        </>
      )}

      {/* Quick-view drawer */}
      <Drawer
        opened={drawerOpen}
        onClose={closeDrawer}
        position="right"
        size="lg"
        title={
          <Text fw={700} size="lg">
            {drawerInvoice?.invoice_number ?? 'Invoice Details'}
          </Text>
        }
      >
        {drawerInvoice ? (
          <Stack gap="md">
            <Group justify="space-between">
              <StatusBadge status={drawerInvoice.status} />
              <Text size="sm" c="dimmed">
                Issued {dayjs(drawerInvoice.issued_date).format('MMM D, YYYY')}
              </Text>
            </Group>

            <SimpleGrid cols={2}>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Due Date</Text>
                <Text size="sm" fw={500}>{dayjs(drawerInvoice.due_date).format('MMM D, YYYY')}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Order</Text>
                <Text size="sm" fw={500}>{drawerInvoice.order_number ?? '—'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Total Amount</Text>
                <Text size="sm" fw={600}>${parseFloat(drawerInvoice.amount).toFixed(2)}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Balance Due</Text>
                <Text size="sm" fw={600} c={parseFloat(drawerInvoice.balance_due) > 0 ? 'red' : 'brand'}>
                  ${parseFloat(drawerInvoice.balance_due).toFixed(2)}
                </Text>
              </div>
            </SimpleGrid>

            {drawerInvoice.lines && drawerInvoice.lines.length > 0 && (
              <>
                <Divider />
                <Text fw={600} size="sm">Line Items</Text>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Description</Table.Th>
                      <Table.Th ta="right">Qty</Table.Th>
                      <Table.Th ta="right">Price</Table.Th>
                      <Table.Th ta="right">Total</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {drawerInvoice.lines.map((line) => (
                      <Table.Tr key={line.id}>
                        <Table.Td><Text size="sm">{line.description}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="sm">{line.quantity}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="sm">${parseFloat(line.unit_price).toFixed(2)}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="sm" fw={500}>${parseFloat(line.total).toFixed(2)}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </>
            )}

            <Divider />
            <Group>
              <Button
                variant="light"
                leftSection={<IconDownload size={16} />}
                onClick={() => handleDownloadPdf(drawerInvoice.id)}
                loading={downloadPdf.isPending}
              >
                Download PDF
              </Button>
              <Button
                component={Link}
                to={`/invoices/${drawerInvoice.id}`}
                variant="subtle"
              >
                Full Details
              </Button>
              {drawerInvoice.status !== 'paid' && parseFloat(drawerInvoice.balance_due) > 0 && (
                <Button
                  component={Link}
                  to={`/billing/pay?invoice=${drawerInvoice.id}`}
                >
                  Pay Now
                </Button>
              )}
            </Group>
          </Stack>
        ) : (
          <Stack gap="xs">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} height={30} radius="md" />
            ))}
          </Stack>
        )}
      </Drawer>
    </Stack>
  );
}
